// CHG-1287 / ITER-3412 — v2 adoption telemetry mount.
//
// Mounted globally in App.tsx (sibling to CommandPalette + WelcomeTrigger).
// Fires session_started on mount + page_view on every route change.
// Cross-component events (welcome_completed, welcome_dismissed,
// classic_fallback_clicked) emit via the global `telemetryClient` export
// below — consumers (WelcomeOverlay, ClassicDashboardLink) import + call.
//
// Privacy: every event payload routes through the typed allowlist in
// events.ts. The backend Pydantic schema rejects unrecognized fields, so
// even an accidental client-side leak fails closed.

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useBrand } from "../contexts/BrandContext";
import { useOrg } from "../contexts/OrgContext";
import { deriveApiBase } from "../lib/runtime-base-urls";
import {
  type AdoptionEvent,
  type AdoptionEventPayload,
  type V2EventName,
  ENDPOINT,
  TelemetryBatch,
  hostnameSignature,
  routeShape,
} from "./events";

// ─────────────────────────────────────────────────────────────────
// Singleton batch + emit() entry point — consumed by other components
// that need to fire ad-hoc events (welcome overlay, classic-link, etc.).
// ─────────────────────────────────────────────────────────────────

function adoptionTelemetryUrl(): string {
  return `${deriveApiBase().replace(/\/+$/, "")}${ENDPOINT}`;
}

export async function postAdoptionTelemetry(events: AdoptionEvent[]): Promise<void> {
  const token = window.localStorage.getItem("auth_token_dev");
  if (!token) return;
  const response = await fetch(adoptionTelemetryUrl(), {
    method: "POST",
    keepalive: true,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ events }),
  });
  if (!response.ok) {
    throw new Error(`adoption telemetry failed: ${response.status}`);
  }
}

const batch = new TelemetryBatch(async (events: AdoptionEvent[]) => {
  await postAdoptionTelemetry(events);
});

let baseContext: { org_id?: string; brand_id?: string; hostname_signature?: string } = {};

export function setTelemetryContext(ctx: typeof baseContext): void {
  baseContext = { ...ctx };
}

export function emitAdoptionEvent(
  event_name: V2EventName,
  payload: AdoptionEventPayload = {},
): void {
  batch.enqueue({
    event_name,
    occurred_at: new Date().toISOString(),
    payload: { ...baseContext, ...payload },
  });
}

export function flushTelemetry(): Promise<void> {
  return batch.flush();
}

// ─────────────────────────────────────────────────────────────────
// AdoptionTelemetry — global mount. Fires session_started once on
// mount + page_view on every route change. Flushes the queue on
// beforeunload so end-of-session events aren't dropped.
// ─────────────────────────────────────────────────────────────────

export function AdoptionTelemetry() {
  const location = useLocation();
  const { variantId } = useBrand();
  const { org } = useOrg();
  const sessionFiredRef = useRef(false);
  const previousRouteRef = useRef<string | null>(null);
  const orgId = org?.id;

  // Update base context whenever org/brand/host shifts.
  useEffect(() => {
    let cancelled = false;
    async function update() {
      const sig =
        typeof window !== "undefined"
          ? await hostnameSignature(window.location.hostname)
          : "";
      if (cancelled) return;
      setTelemetryContext({
        org_id: orgId ?? undefined,
        brand_id: variantId,
        hostname_signature: sig,
      });
    }
    void update();
    return () => {
      cancelled = true;
    };
  }, [orgId, variantId]);

  // session_started — fired once per mount (per browser session).
  useEffect(() => {
    if (sessionFiredRef.current) return;
    sessionFiredRef.current = true;
    void (async () => {
      const referrer =
        typeof document !== "undefined" ? document.referrer : "";
      let referrer_signature: string | undefined;
      try {
        if (referrer) {
          const u = new URL(referrer);
          referrer_signature = await hostnameSignature(u.hostname);
        }
      } catch {
        // ignore — malformed referrer URL
      }
      emitAdoptionEvent("dashboard_v2.session_started", { referrer_signature });
    })();
  }, []);

  // page_view — fired on every distinct route_shape transition.
  useEffect(() => {
    const shape = routeShape(location.pathname);
    if (shape === previousRouteRef.current) return;
    previousRouteRef.current = shape;
    emitAdoptionEvent("dashboard_v2.page_view", {
      route_shape: shape,
      surface_id: surfaceIdFromRouteShape(shape),
    });
  }, [location.pathname]);

  // Flush on beforeunload so end-of-session events ship before tab close.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onUnload = () => {
      void batch.flush();
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, []);

  // CHG-1287 — additive event listeners that pick up signals from
  // CHG-1283 (welcome flags) + CHG-1278 (classic-dashboard link). These
  // CustomEvents fire from the storage.ts setters + the ClassicDashboardLink
  // onClick. Window-level so no component coupling.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const onCompleted = () =>
      emitAdoptionEvent("dashboard_v2.welcome_completed");
    const onDismissed = () =>
      emitAdoptionEvent("dashboard_v2.welcome_dismissed");
    const onClassicFromMenu = (e: Event) => {
      const detail = (e as CustomEvent).detail as { source?: string } | undefined;
      emitAdoptionEvent("dashboard_v2.classic_fallback_clicked", {
        source: detail?.source ?? "profile-menu",
      });
    };

    // Click delegation captures step-4 "Use classic for now" without
    // requiring a mutation to the (immutable) WelcomeOverlay component.
    // Matches the data-action="use-classic" attribute the CHG-1283 anchor
    // already carries.
    const onClickDelegate = (e: MouseEvent) => {
      const t = e.target as Element | null;
      if (!t || !t.closest) return;
      const hit = t.closest('[data-action="use-classic"]');
      if (!hit) return;
      emitAdoptionEvent("dashboard_v2.classic_fallback_clicked", {
        source: "welcome-overlay-step-4",
      });
    };

    window.addEventListener("v2-welcome-completed", onCompleted);
    window.addEventListener("v2-welcome-dismissed", onDismissed);
    window.addEventListener("v2-classic-fallback-clicked", onClassicFromMenu);
    document.addEventListener("click", onClickDelegate, true);
    return () => {
      window.removeEventListener("v2-welcome-completed", onCompleted);
      window.removeEventListener("v2-welcome-dismissed", onDismissed);
      window.removeEventListener("v2-classic-fallback-clicked", onClassicFromMenu);
      document.removeEventListener("click", onClickDelegate, true);
    };
  }, []);

  return null;
}

// Static route_shape → surface_id mapping. Keeps the page_view payload
// from carrying free-form route strings; surface_id is a coarse category
// like "donor" / "campaign" — never an entity ID.
function surfaceIdFromRouteShape(shape: string): string | undefined {
  // First non-empty segment after the leading slash
  const parts = shape.split("/").filter(Boolean);
  if (parts.length === 0) return "overview";
  const top = parts[0];
  const KNOWN: Record<string, string> = {
    donors: "donor",
    assets: "asset",
    campaigns: "campaign",
    recurring: "recurring-donation",
    donations: "donation",
    pledges: "pledge",
    designations: "designation",
    templates: "template",
    team: "team-member",
    events: "event",
    messages: "message",
    imports: "import-batch",
    tags: "tag",
    settings: "settings",
    "reset-password": "auth",
    "verify-email": "auth",
    "accept-invite": "auth",
    auth: "auth",
  };
  return KNOWN[top] ?? "other";
}
